import type { CustomerAreaNavItem, SidebarNavSection } from './customerAreaLayoutTypes'

export const joinClassNames = (...classNames: Array<string | null | undefined | false>) =>
  classNames.filter(Boolean).join(' ')

export const SidebarNavIcon = ({ itemId, active }: { itemId: string; active: boolean }) => {
  const className = joinClassNames(
    'h-3.5 w-3.5 shrink-0',
    active
      ? 'text-(--color-text-primary)'
      : 'text-(--color-text-muted) group-hover:text-(--color-text-secondary)',
  )

  if (itemId === 'orders') {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={className} stroke="currentColor" strokeWidth={1.8} aria-hidden>
        <path d="M3.5 7.5 12 3l8.5 4.5v9L12 21l-8.5-4.5v-9Z" />
        <path d="M3.5 7.5 12 12m8.5-4.5L12 12m0 0v9" />
      </svg>
    )
  }

  if (itemId === 'addresses') {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={className} stroke="currentColor" strokeWidth={1.8} aria-hidden>
        <path d="M12 21s7-5.5 7-11a7 7 0 1 0-14 0c0 5.5 7 11 7 11Z" />
        <circle cx="12" cy="10" r="2.5" />
      </svg>
    )
  }

  if (itemId === 'preferences') {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={className} stroke="currentColor" strokeWidth={1.8} aria-hidden>
        <rect x="3.5" y="6.5" width="17" height="11" rx="2.5" />
        <path d="M3.5 10.5h17" />
      </svg>
    )
  }

  if (itemId === 'profile' || itemId === 'overview') {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={className} stroke="currentColor" strokeWidth={1.8} aria-hidden>
        <circle cx="12" cy="8" r="3.5" />
        <path d="M5 20a7 7 0 0 1 14 0" />
      </svg>
    )
  }

  if (itemId === 'support') {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={className} stroke="currentColor" strokeWidth={1.8} aria-hidden>
        <circle cx="12" cy="12" r="8.5" />
        <path d="M9.8 9.2a2.5 2.5 0 1 1 4.2 1.8c-.8.7-1.5 1.1-1.5 2" />
        <circle cx="12" cy="16.8" r=".8" fill="currentColor" stroke="none" />
      </svg>
    )
  }

  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} stroke="currentColor" strokeWidth={1.8} aria-hidden>
      <path d="M5.5 7.5h13M5.5 12h13M5.5 16.5h13" />
    </svg>
  )
}

export const buildSidebarSections = (navItems: CustomerAreaNavItem[]): SidebarNavSection[] => {
  const sectionOrder: Array<SidebarNavSection['id']> = ['orders', 'account', 'support']
  const grouped = navItems.reduce<Record<SidebarNavSection['id'], CustomerAreaNavItem[]>>(
    (accumulator, item) => {
      const target = item.section ?? 'account'
      accumulator[target].push(item)
      return accumulator
    },
    { orders: [], account: [], support: [] },
  )

  return sectionOrder
    .map((sectionId) => {
      const items = grouped[sectionId]
      const fallbackLabel = sectionId === 'orders' ? 'ORDERS' : sectionId === 'support' ? 'SUPPORT' : 'ACCOUNT'
      return {
        id: sectionId,
        label: items[0]?.sectionLabel ?? fallbackLabel,
        items,
      }
    })
    .filter((section) => section.items.length > 0)
}
